"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef, useState } from "react";
import { FiChevronDown, FiPrinter } from "react-icons/fi";
import Button from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import QuantityStepper from "@/components/ui/quantity-stepper";

// Matches the backend default; the API rejects anything above 50.
const DEFAULT_LABEL_COUNT = 3;
const MAX_LABEL_COUNT = 50;
const MENU_LABEL_COUNTS = [1, 2, 3, 4, 5, 6];
const FIRST_CUSTOM_COUNT = MENU_LABEL_COUNTS.length + 1;

// The typed text, held to the same bounds the stepper enforces on blur.
const parseCount = (raw: string, fallback: number) => {
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) ? Math.min(MAX_LABEL_COUNT, Math.max(1, parsed)) : fallback;
};
const labelsText = (count: number) => `Print ${count} ${count === 1 ? "label" : "labels"}`;
interface PrintLabelsButtonProps {
  onPrint: (numberOfLabels: number) => void;
  isLoading: boolean;
}

/**
 * "Print labels" prints the usual 3 in one click; the arrow beside it offers
 * other counts. Most draws need the default, so the count stays out of the
 * header until someone asks for a different one.
 */
export default function PrintLabelsButton(t0) {
  const $ = _c(32);
  const {
    onPrint,
    isLoading
  } = t0;
  const [isCustomOpen, setIsCustomOpen] = useState(false);
  const openingCustom = useRef(false);
  const customContentRef = useRef(null);
  const arrowRef = useRef(null);
  const returnFocusToArrow = useRef(false);
  let t1;
  if ($[0] !== onPrint) {
    t1 = () => onPrint(DEFAULT_LABEL_COUNT);
    $[0] = onPrint;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FiPrinter className="mr-1.5 h-3.5 w-3.5" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== isLoading || $[4] !== t1) {
    t3 = <Button variant="secondary" size="sm" onClick={t1} isLoading={isLoading}>{t2}Print labels</Button>;
    $[3] = isLoading;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <FiChevronDown className="size-4" aria-hidden="true" />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== isLoading) {
    t5 = <DropdownMenuTrigger asChild={true}><Button ref={arrowRef} variant="secondary" size="sm" className="w-9 justify-center px-0 md:px-0" aria-label="Choose number of labels" disabled={isLoading}>{t4}</Button></DropdownMenuTrigger>;
    $[7] = isLoading;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = event => {
      if (openingCustom.current) {
        event.preventDefault();
        openingCustom.current = false;
      }
    };
    t7 = <DropdownMenuLabel>How many labels?</DropdownMenuLabel>;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  let t8;
  if ($[11] !== onPrint) {
    t8 = MENU_LABEL_COUNTS.map(count => <DropdownMenuItem key={count} hint={count === DEFAULT_LABEL_COUNT ? "Default" : undefined} onSelect={() => onPrint(count)}>{labelsText(count)}</DropdownMenuItem>);
    $[11] = onPrint;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <DropdownMenuSeparator />;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <DropdownMenuItem onSelect={() => {
      openingCustom.current = true;
      setIsCustomOpen(true);
    }}>More…</DropdownMenuItem>;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t8) {
    t11 = <DropdownMenuContent align="end" onCloseAutoFocus={t6}>{t7}{t8}{t9}{t10}</DropdownMenuContent>;
    $[15] = t8;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t11 || $[18] !== t5) {
    t12 = <DropdownMenu>{t5}{t11}</DropdownMenu>;
    $[17] = t11;
    $[18] = t5;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t12 || $[21] !== t3) {
    t13 = <PopoverAnchor asChild={true}><ButtonGroup aria-label="Print collection labels">{t3}{t12}</ButtonGroup></PopoverAnchor>;
    $[20] = t12;
    $[21] = t3;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  let t15;
  let t16;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = event_0 => {
      event_0.preventDefault();
      customContentRef.current?.querySelector("input")?.focus();
    };
    t15 = () => {
      returnFocusToArrow.current = true;
    };
    t16 = event_1 => {
      event_1.preventDefault();
      if (returnFocusToArrow.current) {
        arrowRef.current?.focus();
      }
      returnFocusToArrow.current = false;
    };
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
  } else {
    t14 = $[23];
    t15 = $[24];
    t16 = $[25];
  }
  let t17;
  if ($[26] !== onPrint) {
    t17 = <PopoverContent ref={customContentRef} align="end" className="w-auto min-w-0" onOpenAutoFocus={t14} onEscapeKeyDown={t15} onCloseAutoFocus={t16}><CustomCountForm onPrint={count_0 => {
        setIsCustomOpen(false);
        onPrint(count_0);
      }} /></PopoverContent>;
    $[26] = onPrint;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== isCustomOpen || $[29] !== t13 || $[30] !== t17) {
    t18 = <Popover open={isCustomOpen} onOpenChange={setIsCustomOpen}>{t13}{t17}</Popover>;
    $[28] = isCustomOpen;
    $[29] = t13;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  return t18;
}

/**
 * The More… popover body. It owns its count, and the popover unmounts it on
 * close, so every opening starts at 7: a remembered 40 from the last draw
 * would be one quick confirm away from printing 40 again.
 */
function CustomCountForm(t0) {
  const $ = _c(23);
  const {
    onPrint
  } = t0;
  const [count, setCount] = useState(FIRST_CUSTOM_COUNT);
  let t1;
  if ($[0] !== count || $[1] !== onPrint) {
    t1 = event => {
      if (event.key === "Enter" && event.target instanceof HTMLInputElement) {
        onPrint(parseCount(event.target.value, count));
      }
    };
    $[0] = count;
    $[1] = onPrint;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="text-caption text-foreground-secondary">How many labels?</span>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== count) {
    t3 = <QuantityStepper value={count} onChange={setCount} min={1} max={MAX_LABEL_COUNT} ariaLabel="Number of labels" />;
    $[4] = count;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== count || $[7] !== onPrint) {
    t4 = () => onPrint(count);
    $[6] = count;
    $[7] = onPrint;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <FiPrinter className="mr-1.5 h-3.5 w-3.5" />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== count) {
    t6 = labelsText(count);
    $[10] = count;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <span>{t6}</span>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t4 || $[15] !== t7) {
    t8 = <Button variant="secondary" onClick={t4}>{t5}{t7}</Button>;
    $[14] = t4;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t3 || $[18] !== t8) {
    t9 = <div className="flex items-center gap-2">{t3}{t8}</div>;
    $[17] = t3;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t1 || $[21] !== t9) {
    t10 = <div className="flex flex-col gap-2" onKeyDown={t1}>{t2}{t9}</div>;
    $[20] = t1;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}